// Agent presence and the state of each agent request, from what the engine publishes: the heartbeat (`cloud.sync --watch`,
// with the agent watcher's own beat in `agent_watch`) and the chat's wp_messages (an instruction, the agent's replies and
// its closing status). Freshness thresholds are display choices; every state word is read from the published rows.
import type { AgentWatch, Heartbeat, WpMessage } from "../data/types";
import { ago } from "./format";

/** The engine beats every 30 s (EngineStatus: online ≤ 90 s, slow ≤ 600 s); past STALE it is offline. */
export const ENGINE_OFFLINE_SECONDS = 600;
/** The agent watcher is offline when its last beat is older than this (or missing). */
export const AGENT_ONLINE_SECONDS = 120;

export type PresenceState = "online" | "offline" | "unknown" | "engine_offline";
export interface Presence { state: PresenceState; label: string; detail: string }

export const PRESENCE_LABEL: Record<PresenceState, string> = {
  online: "Agent online · usually replies within a minute or two",
  offline: "Agent offline — your request is queued and runs when the agent watcher is on",
  unknown: "Agent offline — your request is queued and runs when the agent watcher is on",
  engine_offline: "The firm's engine is offline — requests wait until it is back",
};

const secondsSince = (iso: string | null, now: number) => (iso ? (now - Date.parse(iso)) / 1000 : Number.POSITIVE_INFINITY);

/**
 * Is anyone going to pick up a request? The engine's heartbeat first (no fresh beat: the engine is offline), then the agent
 * watcher's beat (`agent_watch`; missing → unknown, treated as offline). The demo has no engine behind it: its synthetic
 * heartbeat is read as published, without the age checks (as the top bar shows "Demo data" instead of an age).
 */
export function agentPresence(beats: Heartbeat[], mode: "demo" | "supabase" | "local", now = Date.now()): Presence {
  const demo = mode === "demo";
  const note = demo ? " Demo data: the heartbeat is synthetic and nothing runs requests in the demo." : "";
  const engine = [...beats].sort((a, b) => b.lastSeen.localeCompare(a.lastSeen))[0];
  if (!engine) return { state: "engine_offline", label: PRESENCE_LABEL.engine_offline, detail: `No engine heartbeat has been published.${note}` };
  const engineAge = secondsSince(engine.lastSeen || null, now);
  if (!demo && !(engineAge <= ENGINE_OFFLINE_SECONDS)) {
    return { state: "engine_offline", label: PRESENCE_LABEL.engine_offline, detail: `${engine.holderName}'s engine last beat ${ago(engine.lastSeen, now)}.` };
  }
  const watch = beats.map((b) => b.agentWatch).filter((w): w is AgentWatch => !!w)
    .sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""))[0];
  if (!watch) return { state: "unknown", label: PRESENCE_LABEL.unknown, detail: `The engine does not publish the agent watcher's state.${note}` };
  const busy = [watch.queued !== null ? `${watch.queued} queued` : null, watch.working !== null ? `${watch.working} being worked` : null].filter(Boolean).join(", ");
  const last = watch.lastReplyAt ? `last reply ${ago(watch.lastReplyAt, now)}` : "no reply yet";
  const fresh = demo || secondsSince(watch.at, now) <= AGENT_ONLINE_SECONDS;
  if (watch.running && watch.at && fresh) {
    return { state: "online", label: PRESENCE_LABEL.online, detail: `Agent watcher running${watch.mode ? ` (${watch.mode})` : ""}; ${busy ? `${busy}; ` : ""}${last}.${note}` };
  }
  const why = !watch.running ? "The agent watcher is not running" : `The agent watcher's last beat was ${watch.at ? ago(watch.at, now) : "never"}`;
  return { state: "offline", label: PRESENCE_LABEL.offline, detail: `${why}.${busy ? ` ${busy}.` : ""}${note}` };
}

export type RequestStateWord = "queued" | "working" | "done" | "failed" | "declined";
export interface RequestState { state: RequestStateWord; pill: string; label: string; reason: string | null }

const isAgent = (m: WpMessage) => m.author === "agent" || m.authorRole === "agent";

/**
 * The state of one agent request, from its instruction message and the messages on it (CONTRACT §1.3, §2): the instruction
 * is `open` until `agent.request-done` sets it `done`; the agent's first reply or status claims it (working). A failed or
 * declined outcome shows only when the engine publishes it on the instruction (`request_outcome`); its reason is the
 * closing status message. Null for anything that is not an instruction.
 */
export function requestState(m: WpMessage, all: WpMessage[]): RequestState | null {
  if (m.kind !== "instruction") return null;
  const replies = all.filter((x) => x.id !== m.id && isAgent(x) && (x.parent === m.id || (m.request !== null && x.request === m.request)))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const closing = [...replies].reverse().find((x) => x.kind === "status") ?? replies[replies.length - 1] ?? null;
  if (m.status === "done" || m.status === "accepted" || m.requestStatus === "done" || m.requestOutcome) {
    const outcome = (m.requestOutcome ?? "done").toLowerCase();
    if (outcome === "failed") return { state: "failed", pill: "failed", label: "Failed", reason: m.requestReason ?? closing?.body ?? "The agent closed the request as failed without a reason." };
    if (outcome === "declined") return { state: "declined", pill: "declined", label: "Declined", reason: m.requestReason ?? closing?.body ?? "The agent declined the request without a reason." };
    return { state: "done", pill: "done", label: "Done", reason: null };
  }
  if (replies.length || m.requestClaimedAt || m.requestStatus === "working") return { state: "working", pill: "in_progress", label: "Picked up — working on it", reason: null };
  return { state: "queued", pill: "waiting", label: "Queued", reason: null };
}
